import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  type GetQConfigStructuresReorderedByExamEventIdAndExamCourseIdQuery,
  ExamEventQConfigStructureCategory,
  type GetExamEventExamineeScoreReportsQuery,
  TagCategory,
  useGetQConfigStructuresReorderedByExamEventIdAndExamCourseIdQuery,
  useGetExamCoursesByExamEventIdQuery,
  useGetExamEventExamineeScoreReportsQuery,
} from '@exam-center/graphql';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

import { useExamEventScoreReportSearchParams } from '../../hooks/use-exam-event-score-report-search-params/use-exam-event-score-report-search-params';

export type ExamEventScoreReport =
  GetExamEventExamineeScoreReportsQuery['ecGetExamEventExamineeScoreReports']['examEventExamineeScoreReports'][number];

export type OrderedQConfigStructure =
  GetQConfigStructuresReorderedByExamEventIdAndExamCourseIdQuery['ecGetQConfigStructuresReorderedByExamEventIdAndExamCourseId']['qConfigStructures'][number];

export interface ExamEventScoreReportContextValue {
  scoreReports: ExamEventScoreReport[];
  orderedQConfigStructures: OrderedQConfigStructure[];
  isLoading: boolean;
  selectedExamCourseId: string;
  selectedCourseLabel: string;
  selectedTagCategory: TagCategory | null;
  hasFilters: boolean;
}

export type ExamEventScoreReportProviderProps = PropsWithChildren;

const ExamEventScoreReportContext = createContext<ExamEventScoreReportContextValue | undefined>(
  undefined,
);

function mapTagCategoryToQConfigStructureCategory(
  tagCategory: TagCategory | null,
): ExamEventQConfigStructureCategory | null {
  if (tagCategory === TagCategory.Part) return ExamEventQConfigStructureCategory.Part;
  if (tagCategory === TagCategory.Subject) return ExamEventQConfigStructureCategory.Subject;

  return null;
}

export default function ExamEventScoreReportProvider(
  props: Readonly<ExamEventScoreReportProviderProps>,
) {
  const { children } = props;
  const {
    examEvent: { id: examEventId },
  } = useExamEventDetails();
  const [{ examCourseId, examCourseLabel, tagCategory }] = useExamEventScoreReportSearchParams();

  const hasFilters = Boolean(examEventId && examCourseId && tagCategory);
  const qConfigStructureCategory = mapTagCategoryToQConfigStructureCategory(tagCategory);

  const { data: examCoursesData, loading: isExamEventCoursesLoading } =
    useGetExamCoursesByExamEventIdQuery({
      variables: {
        input: {
          examEventId,
        },
      },
      skip: !examEventId,
    });

  const selectedCourseLabel = useMemo(() => {
    if (examCourseLabel) return examCourseLabel;

    const examCourses = examCoursesData?.ecGetExamCoursesByExamEventId.examCourses ?? [];
    const selectedCourse = examCourses.find((examCourse) => examCourse.id === examCourseId);

    return selectedCourse?.code ?? '';
  }, [examCourseId, examCourseLabel, examCoursesData]);

  const { data: scoreReportsData, loading: isScoreReportsLoading } =
    useGetExamEventExamineeScoreReportsQuery({
      variables: {
        getExamEventExamineeScoreReportsInput: {
          examEventId,
          examCourseId,
          tagCategory: tagCategory as TagCategory,
        },
      },
      skip: !hasFilters,
    });

  const { data: orderedQConfigStructuresData, loading: isOrderedQConfigStructuresLoading } =
    useGetQConfigStructuresReorderedByExamEventIdAndExamCourseIdQuery({
      variables: {
        input: {
          examEventId,
          examCourseId,
          tagCategory: qConfigStructureCategory as ExamEventQConfigStructureCategory,
        },
      },
      skip: !hasFilters || !qConfigStructureCategory,
    });

  const scoreReports = useMemo(
    () => scoreReportsData?.ecGetExamEventExamineeScoreReports.examEventExamineeScoreReports ?? [],
    [scoreReportsData],
  );

  const orderedQConfigStructures = useMemo(
    () =>
      orderedQConfigStructuresData?.ecGetQConfigStructuresReorderedByExamEventIdAndExamCourseId
        .qConfigStructures ?? [],
    [orderedQConfigStructuresData],
  );

  const contextValue = useMemo<ExamEventScoreReportContextValue>(() => {
    const isLoading =
      isScoreReportsLoading ||
      isOrderedQConfigStructuresLoading ||
      (Boolean(examCourseId) && isExamEventCoursesLoading);

    return {
      scoreReports,
      orderedQConfigStructures,
      isLoading,
      selectedExamCourseId: examCourseId,
      selectedCourseLabel,
      selectedTagCategory: tagCategory,
      hasFilters,
    };
  }, [
    scoreReports,
    orderedQConfigStructures,
    isScoreReportsLoading,
    isOrderedQConfigStructuresLoading,
    examCourseId,
    isExamEventCoursesLoading,
    selectedCourseLabel,
    tagCategory,
    hasFilters,
  ]);

  return (
    <ExamEventScoreReportContext.Provider value={contextValue}>
      {children}
    </ExamEventScoreReportContext.Provider>
  );
}

export { ExamEventScoreReportContext };
